/**
 * Utility functions for data validation and transformation
 */
import { medicineInit, DrRoundFormInit } from './constants';

/**
 * Check if a value is empty (undefined, null, empty array, or empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = value => {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && !Array.isArray(value) && value && Object.keys(value).length === 0)
  );
};

/**
 * Safely convert value to array
 * @param {*} arr - Value to convert
 * @returns {Array} Array or empty array
 */
export const safeArray = arr => (Array.isArray(arr) ? arr : []);

/**
 * Safely convert value to object
 * @param {*} obj - Value to convert
 * @returns {Object} Object or empty object
 */
export const safeObject = obj => (obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {});

/**
 * Transform patient plan data for API submission
 * Converts dataSource array into date-keyed object structure
 * @param {Array} dataSource - Service data array
 * @returns {Object} Transformed service details
 */
export const transformServiceDetails = dataSource => {
  const transformedData = {};

  safeArray(dataSource).forEach(item => {
    Object.keys(safeObject(item)).forEach(key => {
      // Check if key is a date (YYYY-MM-DD format)
      if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        if (!transformedData[key]) {
          transformedData[key] = [];
        }
        transformedData[key].push({
          serviceName: item.serviceName,
          startTime: item.startTime,
          endTime: item.endTime,
          totalMinutes: item[key]?.totalMinutes || '0',
          medicine: item[key]?.medicine || [],
          medicineValue: item[key]?.medicineValue || null,
        });
      }
    });
  });

  return transformedData;
};

/**
 * Transform medicines array into date-keyed object
 * Groups medicines by date for API submission
 * @param {Array} medicines - Medicines array from form
 * @returns {Object} Date-keyed medicines object
 */
export const groupMedicinesByDate = (medicines = []) => {
  const hasMeaningfulMedicineData = (medicine = {}) => {
    const fieldsToCheck = Object.keys(medicine).filter(key => key !== 'date' && key !== '_id');

    return fieldsToCheck.some(key => {
      const value = medicine[key];

      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;

      return true;
    });
  };

  return medicines.reduce((result, medicine) => {
    const { date, ...rest } = medicine;

    if (!date || !hasMeaningfulMedicineData(medicine)) return result;

    if (!result[date]) {
      result[date] = [];
    }

    result[date].push(rest);

    return result;
  }, {});
};
/**
 * Transform medicines from API format to form format
 * Converts date-keyed object to array with date field
 * @param {Object} medicineData - Medicine data from API (date-keyed object)
 * @returns {Array} Array of medicines with date field
 */
export const transformMedicinesFromAPI = medicineData => {
  const medicinesArray = [];

  if (!medicineData || typeof medicineData !== 'object') {
    return medicinesArray;
  }

  Object.keys(medicineData).forEach(date => {
    const medicinesForDate = safeArray(medicineData[date]);
    medicinesForDate.forEach(medicine => {
      // UPDATED: Merge with medicineInit to prevent undefined fields
      medicinesArray.push({
        ...medicineInit,
        ...medicine,
        date: date,
      });
    });
  });

  return medicinesArray;
};

/**
 * Transform diet plans array into date-keyed object
 * Groups diet plans by date for API submission.
 * Based on BE error, each date expects a string (dietName).
 * @param {Array} dietPlans - Diet plans array from form
 * @returns {Object} Date-keyed diet plans object (Date -> String)
 */
export const transformDietPlans = dietPlans => {
  const dietPlansObject = {};

  safeArray(dietPlans).forEach(plan => {
    const date = plan.date;
    if (date && plan.dietName) {
      // Update: Send only the dietName as a string to match BE expectations
      dietPlansObject[date] = plan.dietName;
    }
  });

  return dietPlansObject;
};

/**
 * Transform diet plans from API format to form format
 * Converts date-keyed object (string values) to array with date/id fields
 * @param {Object} dietPlanData - Diet plan data from API (date-keyed object)
 * @returns {Array} Array of diet plans with date field
 */
export const transformDietPlansFromAPI = dietPlanData => {
  const dietPlansArray = [];

  if (!dietPlanData || typeof dietPlanData !== 'object') {
    return dietPlansArray;
  }

  Object.keys(dietPlanData).forEach(date => {
    const dietValue = dietPlanData[date];

    if (typeof dietValue === 'string') {
      // If it's a string, we treat it as the dietName
      dietPlansArray.push({
        _id: '', // ID is not preserved in the string-only format
        dietName: dietValue,
        date: date,
      });
    } else if (dietValue && typeof dietValue === 'object') {
      // Fallback for object or array structures
      const plan = Array.isArray(dietValue) ? dietValue[0] : dietValue;
      if (plan) {
        // UPDATED: Ensure consistent structure
        dietPlansArray.push({
          _id: plan._id || plan.id || '',
          dietName: plan.dietName || (typeof plan === 'string' ? plan : ''),
          date: date,
        });
      }
    }
  });

  return dietPlansArray;
};

/**
 * Transform doctor round form data from nested structure to flat structure
 * Converts: { date: { roundType: { fields } } }
 * To: { date: { fields with roundType } }
 * @param {Object} doctorRoundForm - Doctor round form data from formik
 * @param {string} specificRoundType - Optional: only transform this specific round type (e.g., 'morning')
 * @returns {Object} Flattened doctor round form data
 */
export const transformDoctorRoundForm = (doctorRoundForm, specificRoundType = null) => {
  const transformedData = {};

  if (!doctorRoundForm || typeof doctorRoundForm !== 'object') {
    return transformedData;
  }

  // Iterate through each date
  Object.keys(doctorRoundForm).forEach(date => {
    const dateData = doctorRoundForm[date];

    if (!dateData || typeof dateData !== 'object') {
      return;
    }

    // Check for nested round types (morning, evening, emergency)
    const roundTypes = specificRoundType
      ? [specificRoundType]
      : ['morning', 'evening', 'emergency'];

    roundTypes.forEach(roundType => {
      const roundData = dateData[roundType];
      if (roundData && typeof roundData === 'object' && Object.keys(roundData).length > 0) {
        if (!transformedData[date]) {
          transformedData[date] = {};
        }

        // Clean round data (remove empty fields but keep structure)
        const cleanedRoundData = {};
        Object.keys(roundData).forEach(key => {
          const value = roundData[key];
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value) && value.length === 0) {
              return; // Skip empty arrays
            }
            cleanedRoundData[key] = value;
          }
        });

        if (Object.keys(cleanedRoundData).length > 0) {
          transformedData[date][roundType] = cleanedRoundData;
        }
      }
    });

    // Backup plan: if no nested structure found but dateData has a roundType,
    // treat it as a single round entry (flat format fallback)
    if (!transformedData[date] && dateData.roundType) {
      const cleanedData = {};
      Object.keys(dateData).forEach(key => {
        const value = dateData[key];
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value) && value.length === 0) return;
          cleanedData[key] = value;
        }
      });
      if (Object.keys(cleanedData).length > 0) {
        transformedData[date] = { [dateData.roundType]: cleanedData };
      }
    }
  });

  return transformedData;
};

/**
 * Helper to ensure sparshaSahatva array has all required items in the correct order
 * @param {Array} existingSparsha - Array of sparsha objects from API
 * @returns {Array} Complete sparsha array
 */
const mergeSparshaSahatva = existingSparsha => {
  const baseSparsha = DrRoundFormInit.sparshaSahatva.map(item => ({ ...item }));
  const result = [...baseSparsha];

  if (Array.isArray(existingSparsha)) {
    existingSparsha.forEach(item => {
      const key = Object.keys(safeObject(item))[0];
      if (key) {
        const index = result.findIndex(baseItem => Object.keys(baseItem)[0] === key);
        if (index !== -1) {
          result[index] = { ...item };
        }
      }
    });
  }

  return result;
};

/**
 * Transform doctor round form data from API flat structure to nested UI structure
 * Converts: { date: { fields with roundType } }
 * To: { date: { roundType: { fields } } }
 * This is the reverse of transformDoctorRoundForm
 * @param {Object} doctorRoundFormData - Doctor round form data from API
 * @returns {Object} Nested doctor round form data for UI
 */
export const transformDoctorRoundFormFromAPI = doctorRoundFormData => {
  const transformedData = {};

  if (!doctorRoundFormData || typeof doctorRoundFormData !== 'object') {
    return transformedData;
  }

  const roundTypes = ['morning', 'evening', 'emergency'];

  // Iterate through each date
  Object.keys(doctorRoundFormData).forEach(date => {
    const dateData = doctorRoundFormData[date];

    if (!dateData || typeof dateData !== 'object') {
      return;
    }

    // Check if it's already in nested format
    const isNested = roundTypes.some(type => dateData[type] && typeof dateData[type] === 'object');

    transformedData[date] = {};

    if (isNested) {
      roundTypes.forEach(type => {
        if (dateData[type]) {
          transformedData[date][type] = {
            ...DrRoundFormInit,
            ...dateData[type],
            roundType: type,
            sparshaSahatva: mergeSparshaSahatva(dateData[type].sparshaSahatva),
          };
        }
      });
    } else {
      // It's in flat format
      const roundType = dateData.roundType || 'morning';
      transformedData[date][roundType] = {
        ...DrRoundFormInit,
        ...dateData,
        roundType,
        sparshaSahatva: mergeSparshaSahatva(dateData.sparshaSahatva),
      };
    }
  });

  return transformedData;
};

/**
 * Prepare final submission data
 * Removes empty fields and structures data for API
 * @param {Object} values - Form values
 * @param {Object} patientPlanData - Service details data
 * @param {string} specificRoundType - Optional: only submit this specific round type
 * @returns {Object} Final submission object
 */
export const prepareSubmissionData = (values, patientPlanData, specificRoundType = null) => {
  const finalValue = {
    serviceDetails: transformServiceDetails(patientPlanData?.dataSource),
    doctorRoundForm: transformDoctorRoundForm(values.doctorRoundForm, specificRoundType),
    medicine: groupMedicinesByDate(values.medicines),
    dietPlan: transformDietPlans(values.dietPlans),
    note: values.notes,
    dietPlanNote: values.dietPlanNote,
  };

  // Remove empty fields
  if (isEmpty(finalValue.doctorRoundForm)) delete finalValue.doctorRoundForm;
  if (isEmpty(finalValue.medicine)) delete finalValue.medicine;
  if (isEmpty(finalValue.serviceDetails)) delete finalValue.serviceDetails;
  if (isEmpty(finalValue.dietPlan)) delete finalValue.dietPlan;
  if (isEmpty(finalValue.note) || finalValue.note.trim() === '') delete finalValue.note;
  if (isEmpty(finalValue.dietPlanNote) || finalValue.dietPlanNote.trim() === '')
    delete finalValue.dietPlanNote;

  return finalValue;
};

/**
 * Deep equality check for payload diffing
 * @param {*} a
 * @param {*} b
 * @returns {boolean}
 */
const deepEqual = (a, b) => {
  if (a === b) return true;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (
    a &&
    b &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    !Array.isArray(a) &&
    !Array.isArray(b)
  ) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (let i = 0; i < aKeys.length; i += 1) {
      const key = aKeys[i];
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  return false;
};

/**
 * Recursively keep only changed fields
 * @param {*} nextValue - Newly prepared payload
 * @param {*} prevValue - Existing payload from backend
 * @returns {*} Changed fragment or undefined if unchanged
 */
const getChangedFields = (nextValue, prevValue) => {
  if (prevValue === undefined) {
    return nextValue;
  }

  if (deepEqual(nextValue, prevValue)) {
    return undefined;
  }

  if (Array.isArray(nextValue) || Array.isArray(prevValue)) {
    return nextValue;
  }

  if (
    nextValue &&
    prevValue &&
    typeof nextValue === 'object' &&
    typeof prevValue === 'object' &&
    !Array.isArray(nextValue) &&
    !Array.isArray(prevValue)
  ) {
    const diff = {};
    Object.keys(nextValue).forEach(key => {
      const changed = getChangedFields(nextValue[key], prevValue[key]);
      if (changed !== undefined) {
        diff[key] = changed;
      }
    });
    return Object.keys(diff).length > 0 ? diff : undefined;
  }

  return nextValue;
};

/**
 * Normalize existing service details to match submission structure
 * @param {Object} serviceDetails - Existing service details from backend
 * @returns {Object}
 */
const normalizeServiceDetailsForSubmission = serviceDetails => {
  const normalized = {};

  Object.keys(safeObject(serviceDetails)).forEach(date => {
    const items = safeArray(serviceDetails[date]).map(item => ({
      serviceName: item?.serviceName,
      startTime: item?.startTime,
      endTime: item?.endTime,
      totalMinutes: item?.totalMinutes || '0',
      medicine: item?.medicine || [],
      medicineValue: item?.medicineValue || null,
    }));

    if (items.length > 0) {
      normalized[date] = items;
    }
  });

  return normalized;
};

/**
 * Build normalized payload from existing patient plan for diffing
 * @param {Object} currentPlanData - Current patient plan from backend
 * @param {string} specificRoundType - Optional: only compare this specific round type
 * @returns {Object}
 */
const prepareExistingSubmissionData = (currentPlanData, specificRoundType = null) => {
  const existingDoctorRoundForm = transformDoctorRoundForm(
    transformDoctorRoundFormFromAPI(currentPlanData?.doctorRoundForm),
    specificRoundType
  );

  const existingValue = {
    serviceDetails: normalizeServiceDetailsForSubmission(currentPlanData?.serviceDetails),
    doctorRoundForm: existingDoctorRoundForm,
    medicine: safeObject(currentPlanData?.medicine),
    dietPlan: safeObject(currentPlanData?.dietPlan),
    note: currentPlanData?.note,
    dietPlanNote: currentPlanData?.dietPlanNote,
  };

  if (isEmpty(existingValue.doctorRoundForm)) delete existingValue.doctorRoundForm;
  if (isEmpty(existingValue.medicine)) delete existingValue.medicine;
  if (isEmpty(existingValue.serviceDetails)) delete existingValue.serviceDetails;
  if (isEmpty(existingValue.dietPlan)) delete existingValue.dietPlan;
  if (isEmpty(existingValue.note) || existingValue.note?.trim() === '') delete existingValue.note;
  if (isEmpty(existingValue.dietPlanNote) || existingValue.dietPlanNote?.trim() === '')
    delete existingValue.dietPlanNote;

  return existingValue;
};

/**
 * Check if a value is a date-keyed object (keys are YYYY-MM-DD)
 * @param {*} obj - Value to check
 * @returns {boolean}
 */
const isDateKeyedObject = obj => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  // Check if at least the first key looks like a date
  return /^\d{4}-\d{2}-\d{2}$/.test(keys[0]);
};

/**
 * Get date-level diff for date-keyed objects
 * Only includes dates that have actually changed
 * @param {Object} newObj - New date-keyed data
 * @param {Object} existingObj - Existing date-keyed data
 * @returns {Object|undefined} Object with only changed dates, or undefined if no changes
 */
const getDateLevelDiff = (newObj, existingObj) => {
  const safeNew = safeObject(newObj);
  const safeExisting = safeObject(existingObj);
  const diff = {};

  // Check dates in new payload
  Object.keys(safeNew).forEach(date => {
    if (!deepEqual(safeNew[date], safeExisting[date])) {
      diff[date] = safeNew[date];
    }
  });

  return Object.keys(diff).length > 0 ? diff : undefined;
};

// Fields that use date-keyed structure
const DATE_KEYED_FIELDS = ['serviceDetails', 'medicine', 'dietPlan', 'doctorRoundForm'];

/**
 * Prepare payload with only changed fields for PATCH update
 * Uses date-level diffing for date-keyed fields to minimize payload size
 * @param {Object} values - Current form values
 * @param {Object} patientPlanData - Service details data
 * @param {Object} currentPlanData - Existing plan from backend
 * @param {string} specificRoundType - Optional: only submit this specific round type
 * @returns {Object} Diff payload with changed fields only
 */
export const prepareChangedSubmissionData = (
  values,
  patientPlanData,
  currentPlanData,
  specificRoundType = null
) => {
  const newPayload = prepareSubmissionData(values, patientPlanData, specificRoundType);
  const existingPayload = prepareExistingSubmissionData(currentPlanData, specificRoundType);

  const diff = {};

  Object.keys(newPayload).forEach(key => {
    const newVal = newPayload[key];
    const existingVal = existingPayload[key];

    // For date-keyed fields, diff at the individual date level
    if (DATE_KEYED_FIELDS.includes(key) && isDateKeyedObject(newVal)) {
      const dateDiff = getDateLevelDiff(newVal, existingVal);
      if (dateDiff) {
        if (key === 'dietPlan') {
          // Send the full newVal for dietPlan to prevent backend from deleting other dates
          diff[key] = newVal;
        } else {
          diff[key] = dateDiff;
        }
      }
    } else {
      // For non-date fields (note, dietPlanNote), send if changed
      if (!deepEqual(newVal, existingVal)) {
        diff[key] = newVal;
      }
    }
  });

  return diff;
};

/**
 * Split a large diff payload into smaller chunks based on date ranges.
 * Each chunk will contain at most `maxDatesPerChunk` unique dates across all date-keyed fields.
 * Non-date fields (e.g., note, dietPlanNote) are included in the first chunk only.
 *
 * @param {Object} diffPayload - The diff payload from prepareChangedSubmissionData
 * @param {number} maxDatesPerChunk - Maximum number of dates per chunk (default: 15)
 * @returns {Array<Object>} Array of payload chunks
 */
export const splitPayloadByDateChunks = (diffPayload, maxDatesPerChunk = 15) => {
  if (!diffPayload || Object.keys(diffPayload).length === 0) {
    return [];
  }

  // Collect all unique dates across all date-keyed fields
  const allDates = new Set();
  const nonDateFields = {};

  Object.keys(diffPayload).forEach(key => {
    // Treat 'dietPlan' as non-chunkable to keep all its date values together in the first chunk
    if (
      key !== 'dietPlan' &&
      DATE_KEYED_FIELDS.includes(key) &&
      isDateKeyedObject(diffPayload[key])
    ) {
      Object.keys(diffPayload[key]).forEach(date => allDates.add(date));
    } else {
      nonDateFields[key] = diffPayload[key];
    }
  });

  const sortedDates = Array.from(allDates).sort();

  // If total dates fit in one chunk, return as-is
  if (sortedDates.length <= maxDatesPerChunk) {
    return [diffPayload];
  }

  // Split dates into chunks
  const chunks = [];
  for (let i = 0; i < sortedDates.length; i += maxDatesPerChunk) {
    const chunkDates = sortedDates.slice(i, i + maxDatesPerChunk);
    const chunkDateSet = new Set(chunkDates);
    const chunkPayload = {};

    // Include non-date fields only in the first chunk
    if (i === 0) {
      Object.assign(chunkPayload, nonDateFields);
    }

    // Include only the dates belonging to this chunk for each date-keyed field
    DATE_KEYED_FIELDS.forEach(key => {
      // Skip 'dietPlan' as it is already fully included in nonDateFields (first chunk)
      if (key !== 'dietPlan' && diffPayload[key] && isDateKeyedObject(diffPayload[key])) {
        const fieldChunk = {};
        Object.keys(diffPayload[key]).forEach(date => {
          if (chunkDateSet.has(date)) {
            fieldChunk[date] = diffPayload[key][date];
          }
        });
        if (Object.keys(fieldChunk).length > 0) {
          chunkPayload[key] = fieldChunk;
        }
      }
    });

    if (Object.keys(chunkPayload).length > 0) {
      chunks.push(chunkPayload);
    }
  }

  return chunks;
};
